import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialAdminControllerService,
  PaymentExpirationConfig,
  PaymentExpirationConfigLogDetail
} from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-pix',
  templateUrl: './pix.component.html',
  styleUrl: './pix.component.scss',
  providers: [MessageService]
})
export class PixComponent implements OnInit {
  private title = inject(Title);
  private financialService = inject(FinancialAdminControllerService);
  private messageService = inject(MessageService);

  configs: PaymentExpirationConfig[] = [];
  logs: { [id: string]: PaymentExpirationConfigLogDetail[] };
  logCols = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Alteração')
      .setField('')
      .setDisplayFunction((l: PaymentExpirationConfigLogDetail) => {
        let changes = '';
        if (l.oldName || l.newName)
          changes += `Nome: "${l.oldName || 'vazio'}" para "${l.newName || 'vazio'}"<br>`;
        if (l.oldDescription || l.newDescription)
          changes += `Descrição: "${l.oldDescription || 'vazio'}" para "${l.newDescription || 'vazio'}"<br>`;
        if (l.oldExpirationTime || l.newExpirationTime)
          changes += `Tempo de expiração: "${l.oldExpirationTime || 'vazio'}" para "${l.newExpirationTime || 'vazio'}"<br>`;
        return changes || 'Nenhuma alteração';
      })
      .build(),
    new TableColumn.Builder()
      .setHeader('Usuário')
      .setField('userName')
      .setCondition('contains')
      .build()
  ];
  forms: {
    [id: string]: FormGroup<{
      name: FormControl<string>;
      description: FormControl<string>;
      expirationTime: FormControl<number>;
      enumValue: FormControl<string>;
    }>;
  };
  maxExpirationTime = 86400; // 24 hours in seconds

  async ngOnInit() {
    this.title.setTitle('Configurações do Pix');
    LoaderService.showLoader();
    await Promise.all([this.findConfigs(), this.findLogs()]);
    LoaderService.showLoader(false);
  }

  async findConfigs() {
    try {
      this.config = await lastValueFrom(
        this.financialService
          .getPaymentExpirationConfigs()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findLogs() {
    try {
      this.logs = await lastValueFrom(
        this.financialService.getPaymentExpirationConfigLogs().pipe(
          map((data) =>
            data.result.reduce(
              (logs, log) => {
                if (!logs[log.paymentExpirationConfigId])
                  logs[log.paymentExpirationConfigId] = [log];
                else logs[log.paymentExpirationConfigId].push(log);
                return logs;
              },
              {} as { [id: string]: PaymentExpirationConfigLogDetail[] }
            )
          )
        )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async saveForm(paymentExpirationConfigId: number) {
    if (this.forms[paymentExpirationConfigId.toString()].valid)
      try {
        LoaderService.showLoader();
        await lastValueFrom(
          this.financialService.updatePaymentExpirationConfig(
            this.forms[paymentExpirationConfigId.toString()].value,
            paymentExpirationConfigId
          )
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Configuração salva com sucesso'
        });
        await this.findLogs();
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  set config(configs: PaymentExpirationConfig[]) {
    this.configs = configs;
    this.forms = configs.reduce(
      (group, config) => {
        group[config.paymentExpirationConfigId] = new FormGroup({
          name: new FormControl(config.name, Validators.required),
          description: new FormControl(config.description, Validators.required),
          expirationTime: new FormControl(config.expirationTime, [
            Validators.required,
            Validators.min(60),
            Validators.max(this.maxExpirationTime)
          ]),
          enumValue: new FormControl({
            value: config.enumValue,
            disabled: true
          })
        });
        return group;
      },
      {} as { [key: string]: FormGroup }
    );
  }

  expirationLabel(paymentExpirationConfigId: number) {
    const seconds =
      this.forms[paymentExpirationConfigId.toString()]?.value.expirationTime;
    if (seconds < 60) return null;
    if (seconds === 60) return '1 minuto';
    if (seconds < 3600) return `${seconds / 60} minutos`;
    return `${seconds / 3600} horas`;
  }
}
